import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams, HttpResponseBase, HttpHeaders } from '@angular/common/http';
import { Person, CreatePerson } from '../models/person';
import { EmployeeStatus } from '../models/person-status';
import { ApiResponse } from '../models/api-response';
import { PagedPayload } from '../models/paged-payload';
import { EmployeeEarning, EmployeeEarningRate } from '../models/employee-earning';
import { EmployeeDeductionRate, EmployeeDeduction } from '../models/employee-deductions';
import {
  EmployeeDirectDeposit,
  EmployeeDirectDepositExtended
} from '../models/employee-direct-deposit';
import { EmployeeTax, EmployeeTaxExemptions, EmployeeTaxes } from '../models/employee-tax';
import { EmergencyContact } from '../models/emergency-contact';
import {
  EmployeeLaborAllocation,
  EmployeeLaborAllocationHistory
} from '../models/labor-allocation';
import { EmployeePTO } from '../models/paid-time-off';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PeopleService {
  constructor(
    private http: HttpClient,
    @Inject('env') private env: any,
    @Inject('errorInterceptorSkipHeader') private errorInterceptorSkipHeader: any
  ) {}

  ////  EMPLOYEE METHODS ////

  // create employee status
  public addStatus(
    clientId: string,
    personId: number,
    status: EmployeeStatus
  ): Observable<ApiResponse<EmployeeStatus>> {
    return this.http.post<ApiResponse<EmployeeStatus>>(
      `${this._personUrl(clientId, personId)}/Statuses`,
      status
    );
  }

  // create person
  public createPerson(clientId: string, person: CreatePerson): Observable<ApiResponse<Person>> {
    return this.http.post<ApiResponse<Person>>(
      `${this.env.apiUrlRoot}/Companies/${clientId}/People`,
      person
    );
  }

  // delete person
  public deletePerson(clientId: string, personId: number): Observable<HttpResponseBase> {
    return this.http.delete<HttpResponseBase>(`${this._personUrl(clientId, personId)}`);
  }

  // get person
  public getPerson(clientId: string, personId: number): Observable<Person> {
    return this.http.get<Person>(this._personUrl(clientId, personId));
  }

  public getActiveStatus(statuses: EmployeeStatus[]) {
    return [...statuses].sort((a: EmployeeStatus, b: EmployeeStatus) => {
      return b.SequenceNumber - a.SequenceNumber;
    })[0];
  }

  // get employee statuses
  public getStatuses(clientId: string, personId: number): Observable<EmployeeStatus[]> {
    return this.http.get<EmployeeStatus[]>(`${this._personUrl(clientId, personId)}/Statuses`);
  }

  public searchPeople(
    clientId: string,
    args?: {
      firstName?: string;
      lastName?: string;
      employeeNumber?: number;
      query?: string;
      pageNumber?: number;
      perPage?: number;
      status?: string;
    }
  ): Observable<PagedPayload<Person>> {
    // default our search args pagination if no args given
    args = args || {
      pageNumber: 1,
      perPage: 50
    };

    // if args were given, ensure we have pagination properties
    if (!args.pageNumber) {
      args.pageNumber = 1;
    }

    if (!args.perPage) {
      args.perPage = 50;
    }

    let params = new HttpParams();

    if (args.firstName) {
      params = params.set('first_name', args.firstName.trim());
    }

    if (args.lastName) {
      params = params.set('last_name', args.lastName.trim());
    }

    if (args.employeeNumber) {
      params = params.set('employee_number', args.employeeNumber.toString());
    }

    if (args.status) {
      params = params.set('status', args.status.trim());
    }

    if (args.query) {
      params = params.set('query', args.query.trim());
    }

    params = params.set('page_number', args.pageNumber.toString());
    params = params.set('per_page', args.perPage.toString());

    return this.http.get<PagedPayload<Person>>(
      `${this.env.apiUrlRoot}/Companies/${clientId}/People`,
      { params: params }
    );
  }

  // update person
  public updatePerson(clientId: string, person: Person): Observable<ApiResponse<Person>> {
    return this.http.put<ApiResponse<Person>>(`${this._personUrl(clientId, person.Id)}`, person);
  }

  public updateStatus(
    clientId: string,
    personId: number,
    seqNo: number,
    status: EmployeeStatus
  ): Observable<ApiResponse<EmployeeStatus>> {
    return this.http.put<ApiResponse<EmployeeStatus>>(
      `${this._personUrl(clientId, personId)}/Statuses/${seqNo}`,
      status
    );
  }

  //// EARNINGS METHODS ////

  // FIXME: remove?
  public createEarning(
    clientId: string,
    personId: number,
    earning: EmployeeEarning
  ): Observable<ApiResponse<EmployeeEarning>> {
    return this.http.post<ApiResponse<EmployeeEarning>>(
      `${this._personUrl(clientId, personId)}/Earnings`,
      earning
    );
  }

  public createEarningRate(
    clientId: string,
    personId: number,
    earningId: string,
    rate: EmployeeEarningRate
  ): Observable<ApiResponse<EmployeeEarningRate>> {
    return this.http.post<ApiResponse<EmployeeEarningRate>>(
      `${this._personUrl(clientId, personId)}/Earnings/${earningId}/Rate`,
      rate
    );
  }

  public getEmployeeEarnings(clientId: string, personId: number): Observable<EmployeeEarning[]> {
    return this.http.get<EmployeeEarning[]>(`${this._personUrl(clientId, personId)}/Earnings`);
  }

  public getSingleEarning(
    clientId: string,
    personId: number,
    earningId: string
  ): Observable<EmployeeEarning> {
    return this.http.get<EmployeeEarning>(
      `${this._personUrl(clientId, personId)}/Earnings/${earningId}`
    );
  }

  public updateEarning(
    clientId: string,
    personId: number,
    earning: EmployeeEarning
  ): Observable<ApiResponse<EmployeeEarning>> {
    return this.http.put<ApiResponse<EmployeeEarning>>(
      `${this._personUrl(clientId, personId)}/Earnings/${earning.Id}`,
      earning
    );
  }

  public updateEarningRate(
    clientId: string,
    personId: number,
    earningId: string,
    rate: EmployeeEarningRate
  ): Observable<ApiResponse<EmployeeEarningRate>> {
    return this.http.put<ApiResponse<EmployeeEarningRate>>(
      `${this._personUrl(clientId, personId)}/Earnings/${earningId}/Rate/${rate.SequenceNumber}`,
      rate
    );
  }

  //// DEDUCTIONS METHODS ////

  public createDeductionRate(
    clientId: string,
    personId: number,
    deductionId: string,
    rate: EmployeeDeductionRate
  ): Observable<ApiResponse<EmployeeDeductionRate>> {
    return this.http.post<ApiResponse<EmployeeDeductionRate>>(
      `${this._personUrl(clientId, personId)}/Deductions/${deductionId}/Rate`,
      rate
    );
  }

  public getEmployeeDeductions(
    clientId: string,
    personId: number
  ): Observable<EmployeeDeduction[]> {
    return this.http.get<EmployeeDeduction[]>(`${this._personUrl(clientId, personId)}/Deductions`);
  }

  public getDeductionRate(
    clientId: string,
    personId: number,
    deductionId: string,
    rateId: number
  ): Observable<EmployeeDeductionRate> {
    return this.http.get<EmployeeDeductionRate>(
      `${this._personUrl(clientId, personId)}/Deductions/${deductionId}/Rate/${rateId}`
    );
  }

  public getSingleDeduction(
    clientId: string,
    personId: number,
    deductionId: string
  ): Observable<EmployeeDeduction> {
    return this.http.get<EmployeeDeduction>(
      `${this._personUrl(clientId, personId)}/Deductions/${deductionId}`
    );
  }

  public updateEmployeeDeduction(
    clientId: string,
    personId: number,
    deduction: EmployeeDeduction
  ): Observable<ApiResponse<EmployeeDeduction>> {
    return this.http.put<ApiResponse<EmployeeDeduction>>(
      `${this._personUrl(clientId, personId)}/Deductions/${deduction.Id}`,
      deduction
    );
  }

  public updateDeductionRate(
    clientId: string,
    personId: number,
    deductionId: string,
    rate: EmployeeDeductionRate
  ): Observable<ApiResponse<EmployeeDeductionRate>> {
    return this.http.put<ApiResponse<EmployeeDeductionRate>>(
      `${this._personUrl(clientId, personId)}/Deductions/${deductionId}/Rate/${
        rate.SequenceNumber
      }`,
      rate
    );
  }

  //// DIRECT DEPOSIT METHODS ////

  public getDirectDeposits(
    clientId: string,
    personId: number
  ): Observable<EmployeeDirectDepositExtended[]> {
    return this.http.get<EmployeeDirectDepositExtended[]>(
      `${this._personUrl(clientId, personId)}/DirectDeposits`
    );
  }

  public setDirectDeposits(clientId: string, personId: number, deposits: EmployeeDirectDeposit[]) {
    return this.http.put<ApiResponse<EmployeeDirectDepositExtended>>(
      `${this._personUrl(clientId, personId)}/DirectDeposits`,
      deposits
    );
  }

  //// TAX METHODS ////

  public createEmployeeTaxExemptions(
    clientId: string,
    personId: number,
    exemptions: EmployeeTaxExemptions
  ): Observable<ApiResponse<EmployeeTaxExemptions>> {
    return this.http.post<ApiResponse<EmployeeTaxExemptions>>(
      `${this._personUrl(clientId, personId)}/TaxExemptions`,
      exemptions
    );
  }

  public getEmployeeTaxes(clientId: string, personId: number): Observable<EmployeeTaxes> {
    return this.http.get<EmployeeTaxes>(`${this._personUrl(clientId, personId)}/Taxes`);
  }

  public getEmployeeTaxExemptions(
    clientId: string,
    personId: number
  ): Observable<ApiResponse<EmployeeTaxExemptions>> {
    return this.http.get<ApiResponse<EmployeeTaxExemptions>>(
      `${this._personUrl(clientId, personId)}/TaxExemptions`
    );
  }

  public updateEmployeeTaxes(
    clientId: string,
    personId: number,
    taxes: EmployeeTaxes,
    ignoreHeader?: boolean
  ): Observable<ApiResponse<EmployeeTaxes>> {
    let headers = new HttpHeaders();

    if (ignoreHeader) {
      headers = headers.set(this.errorInterceptorSkipHeader, '');
    }

    return this.http.put<ApiResponse<EmployeeTaxes>>(
      `${this._personUrl(clientId, personId)}/Taxes`,
      taxes,
      { headers }
    );
  }

  public updateEmployeeTaxExemptions(
    clientId: string,
    personId: number,
    exemptions: EmployeeTaxExemptions
  ): Observable<ApiResponse<EmployeeTaxExemptions>> {
    return this.http.put<ApiResponse<EmployeeTaxExemptions>>(
      `${this._personUrl(clientId, personId)}/TaxExemptions`,
      exemptions
    );
  }

  // #region EMERGENCY CONTACT METHODS

  public deleteEmergencyContact(clientId: string, personId: number, contactId: number) {
    return this.http.delete(
      `${this._personUrl(clientId, personId)}/EmergencyContacts/${contactId}`
    );
  }

  public createEmergencyContact(
    clientId: string,
    personId: number,
    contact: EmergencyContact
  ): Observable<ApiResponse<EmergencyContact>> {
    return this.http.post<ApiResponse<EmergencyContact>>(
      `${this._personUrl(clientId, personId)}/EmergencyContacts`,
      contact
    );
  }

  public getEmergencyContacts(clientId: string, personId: number): Observable<EmergencyContact[]> {
    return this.http.get<EmergencyContact[]>(
      `${this._personUrl(clientId, personId)}/EmergencyContacts`
    );
  }

  public updateEmergencyContact(
    clientId: string,
    personId: number,
    contact: EmergencyContact
  ): Observable<ApiResponse<EmergencyContact>> {
    return this.http.put<ApiResponse<EmergencyContact>>(
      `${this._personUrl(clientId, personId)}/EmergencyContacts/${contact.Id}`,
      contact
    );
  }

  // #endregion

  // #region LABOR ALLOCATION METHODS

  public updateLaborAllocations(
    clientId: string,
    personId: number,
    allocations: EmployeeLaborAllocation[]
  ): Observable<ApiResponse<EmployeeLaborAllocation[]>> {
    return this.http.post<ApiResponse<EmployeeLaborAllocation[]>>(
      `${this._personUrl(clientId, personId)}/LaborAllocations`,
      allocations
    );
  }

  public getLaborAllocations(
    clientId: string,
    personId: number
  ): Observable<EmployeeLaborAllocation[]> {
    return this.http.get<EmployeeLaborAllocation[]>(
      `${this._personUrl(clientId, personId)}/LaborAllocations`
    );
  }

  public getLaborAllocationHistory(
    clientId: string,
    personId: number
  ): Observable<EmployeeLaborAllocationHistory[]> {
    return this.http.get<EmployeeLaborAllocationHistory[]>(
      `${this._personUrl(clientId, personId)}/EmployeeLaborAllocationHistory`
    );
  }

  // #endregion

  // #region PTO METHODS

  public getEmployeePTOs(clientId: string, personId: number): Observable<EmployeePTO[]> {
    return this.http.get<EmployeePTO[]>(`${this._personUrl(clientId, personId)}/PTO`);
  }
  public createEmployeePTO(
    clientId: string,
    personId: number,
    pto: EmployeePTO
  ): Observable<ApiResponse<EmployeePTO>> {
    return this.http.post<ApiResponse<EmployeePTO>>(
      `${this._personUrl(clientId, personId)}/PTO`,
      pto
    );
  }
  public updateEmployeePTO(
    clientId: string,
    personId: number,
    pto: EmployeePTO
  ): Observable<ApiResponse<EmployeePTO>> {
    return this.http.put<ApiResponse<EmployeePTO>>(
      `${this._personUrl(clientId, personId)}/PTO/${pto.Id}`,
      pto
    );
  }

  // #endregion

  // #region PRIVATE  METHODS

  private _personUrl(clientId: string, personId: number): string {
    return `${this.env.apiUrlRoot}/Companies/${clientId}/People/${personId}`;
  }

  // #endregion
}
